import { finalize } from 'rxjs/operators';
import {
  CHECK_MISSING_DOCUMENTS,
  CREATE_NEW_FORM,
  GENERATE_FORM_101_AS_PDF,
  GET_CITIES,
  DOWNLOAD_FORM_119,
  GET_FORM101_AS_PDF,
  GET_FORM101_DATA,
  GET_FORM_HEADERS,
  GET_SETTLEMENTS,
  REINIT_AUTHENTICATION_TOKEN,
  SAVE_AS_DRAFT,
  SET_CITIES,
  SET_CREATE_FORM_ERRORS,
  SET_CREATE_FORM_LOADING,
  SET_EDIT_FORM_ERRORS,
  SET_FORM101_DATA,
  SET_FORM101_ID,
  SET_FORM101_META_DATA,
  SET_FORM_ERRORS,
  SET_FORM_ERRORS_OPEN,
  SET_FORM_ERRORS_SHOW,
  SET_FORM_HEADERS,
  SET_FORM_LOADING_MODE,
  SET_FORM_SUCCESS_DIALOG,
  SET_NO_PDF_ERROR,
  SET_OPEN_CONFIRM_FORM_DIALOG,
  SET_SETTLEMENTS,
  SET_SUBMIT_FORM_ERRORS,
  SUBMIT_FORM,
  UPDATE_USER_OFFICE,
  COMPARE_EMPLOYEE_DETAILS,
  COMPARE_EMPLOYEE_DETAILS_ERRORS,
  SET_OPEN_DELETE_FORM_DIALOG,
  SET_APPROVE_DETAILS_CHANGES,
  DELETE_FORM_WITH_CHANGES,
  SET_SKIP_SAVE_BEFORE_LEAVE_WIZARD_MODAL,
  SET_DELETE_FORM_ERRORS,
} from '@/types/form.types';
import { ActionContext, ActionTree } from 'vuex';
import { FormState } from '@/store/modules/form';
import { RootState } from '@/store';
import { Utils } from '@/globals/Utils';
import { eventsHub } from '@/main';
import FormService from '@/services/form.service';
import { DOCUMENT_ROOT, SET_MISSING_DOCUMENTS } from '@/types/documents.types';
import { FileMimeType } from '@/models/FileMimeType';
import { AUTHORIZATION_ROOT, SET_TOKENS } from '@/types/authorization.types';
import { SET_GLOBAL_ERROR } from '@/types/store.types';
import { AUTHENTICATION_ROOT, USER_DETAILS } from '@/types/authentication.types';
import { Form101DataResponse } from '@/models/form/Form101DataResponse.model';

export const formService = new FormService();

export const actions: ActionTree<FormState, RootState> = {

  [GET_FORM_HEADERS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
  ) => {
    commit(SET_FORM_HEADERS, { results: [], resultCount: 0 });
    commit(SET_FORM_LOADING_MODE, true);
    formService.getFormHeaders()
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          commit(SET_FORM_HEADERS, data);
        },
      );
  },

  [GET_FORM101_AS_PDF]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    docId: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    commit(SET_NO_PDF_ERROR, false);
    formService.getForm101AsPdf(docId)
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          if (data.fileData) {
            Utils.downloadBase64File(data.fileData, 'FORM101_' + docId, FileMimeType.PDF);
          }
        },
        error => {
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('form101.wizard.formDownloadErrorMessage'),
          });
        },
      );
  },

  [DOWNLOAD_FORM_119]: (
    { commit }: ActionContext<FormState, RootState>,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    formService.getDownload119Form()
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          if (data.fileData) {
            Utils.downloadBase64File(data.fileData, data.fileName);
          }
        },
        error => {
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('form101.wizard.formDownloadErrorMessage ' + error),
          });
        },
      );
  },

  [GENERATE_FORM_101_AS_PDF]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    formId: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    commit(SET_NO_PDF_ERROR, false);
    formService.generateForm101AsPdf(formId)
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          if (data.fileData) {
            Utils.downloadBase64File(data.fileData, 'FORM101_' + formId, FileMimeType.PDF);
          }
        },
        error => {
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('form101.wizard.formDownloadErrorMessage'),
          });
        },
      );
  },

  [GET_FORM101_DATA]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    formId: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    formService.getFormData(formId)
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          commit(SET_FORM101_DATA, data.formData);
          commit(SET_FORM101_ID, data.formData.formId);
          commit(SET_FORM101_META_DATA, data.formMetadata);
          if (state.formReadOnlyMode) {
            eventsHub.$router.push({ name: 'wizard' });
          } else {
            formService.editExistingForm(formId).subscribe((data) => {
                eventsHub.$router.push({ name: 'wizard' });
              },
              error => {
                commit(SET_CREATE_FORM_ERRORS, error.messages);
                commit(SET_EDIT_FORM_ERRORS, error.messages);
              });
          }
        },
        error => {
          commit(SET_CREATE_FORM_ERRORS, error.messages);
          commit(SET_EDIT_FORM_ERRORS, error.messages);
        },
      );
  },

  [COMPARE_EMPLOYEE_DETAILS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    formId: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    formService.compareEmployeeDetails(formId)
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          if (data.formDetailsChanged) {
            commit(SET_OPEN_DELETE_FORM_DIALOG, true);
          }
        },
        error => {
          commit(COMPARE_EMPLOYEE_DETAILS_ERRORS, error.messages);
        },
      );
  },

  [DELETE_FORM_WITH_CHANGES]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    formId: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    formService.deleteFormWithChanges(formId)
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        (data) => {
          eventsHub.$notify({
            group: 'frd',
            type: 'success',
            text: '' + eventsHub.$t('form101.wizard.formRemoveModal.formRemoveModalSuccessfullyMessage'),
          });
          commit(SET_OPEN_DELETE_FORM_DIALOG, false);
          commit(SET_SKIP_SAVE_BEFORE_LEAVE_WIZARD_MODAL, true);
          eventsHub.$router.push({ name: 'lobby' });
        },
        error => commit(SET_DELETE_FORM_ERRORS, error.messages),
      );
  },

  [CREATE_NEW_FORM]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    taxYear: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    commit(SET_CREATE_FORM_LOADING, true);
    formService.createNewForm(taxYear)
      .pipe(
        finalize(() => {
          commit(SET_FORM_LOADING_MODE, false);
          commit(SET_CREATE_FORM_LOADING, false);
        }),
      )
      .subscribe(
        (data) => {
          commit(SET_FORM101_ID, data.formData.formId);
          commit(SET_FORM101_DATA, data.formData);
          commit(SET_FORM101_META_DATA, data.formMetadata);
          eventsHub.$router.push({ name: 'wizard' });
        },
        error => {
          commit(SET_CREATE_FORM_ERRORS, error.messages);
          const $tr = document.querySelectorAll('#os tbody tr')[0];
          const offices = rootGetters[AUTHENTICATION_ROOT + USER_DETAILS].offices;
          if ((!offices || offices.length <= 2) && $tr) {
            $tr!.classList.add('fill-new-form-error');
            setTimeout(() => {
              $tr!.classList.remove('fill-new-form-error');
            }, 5000);
          } else {
            eventsHub.$notify({
              group: 'frd',
              type: 'error',
              text: '' + eventsHub.$t('form101.new_form_modal.cant_create_new_form'),
            });
          }
        },
      );
  },

  [REINIT_AUTHENTICATION_TOKEN]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    taxYear: number,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    commit(SET_CREATE_FORM_LOADING, true);
    formService.reinitAuthenticationToken()
      .subscribe(
        (data) => {
          commit(AUTHORIZATION_ROOT + SET_TOKENS,
            { accessToken: data.accessToken, refreshToken: data.refreshToken },
            { root: true },
          );
          dispatch(CREATE_NEW_FORM, taxYear);
        },
        error => {
          commit(SET_FORM_LOADING_MODE, false);
          commit(SET_CREATE_FORM_LOADING, false);
          eventsHub.$notify({
            group: 'frd',
            type: 'error',
            text: '' + eventsHub.$t('form101.new_form_modal.cant_create_new_form'),
          });
        },
      );
  },

  [SUBMIT_FORM]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    { password ,effectiveDate }: { password: string, effectiveDate: Date },
  ) => {
    if (state.form101Data.ranAutoFill) {
      commit(SET_FORM101_DATA, { ...state.form101Data, ['runTaxBenefitsAutoFill']: false });
    }
    commit(SET_SUBMIT_FORM_ERRORS, []);
    commit(SET_FORM_LOADING_MODE, true);
    formService.submitForm(password, effectiveDate, state.form101Data)
      .pipe(
        finalize(() => commit(SET_FORM_LOADING_MODE, false)),
      )
      .subscribe(
        () => {
          commit(SET_OPEN_CONFIRM_FORM_DIALOG, false);
          commit(SET_FORM_SUCCESS_DIALOG, true);
          eventsHub.$router.push({ name: 'lobby' });
          // commit(SET_OPEN_SAVE_BEFORE_LEAVE_WIZARD_MODAL, false);


        },
        error => {
          const loginFromSso = rootGetters[AUTHENTICATION_ROOT + USER_DETAILS].loginFromSso;
          if (loginFromSso) {
            commit(SET_FORM_ERRORS, error.validationMessages || error.messages);
            commit(SET_FORM_ERRORS_SHOW, true);
            commit(SET_FORM_ERRORS_OPEN, true);
          } else {
            commit(SET_SUBMIT_FORM_ERRORS, error.messages);
          }
        },
      );
  },

  [SAVE_AS_DRAFT]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    isFinal?: boolean,
  ) => {
    if (state.form101Data.ranAutoFill) {
      commit(SET_FORM101_DATA, { ...state.form101Data, ['runTaxBenefitsAutoFill']: false });
    }
    // commit(RESET_FORM_ERRORS);
    commit(SET_FORM_LOADING_MODE, true);
    formService.saveAsDraft(state.form101Data)
      .pipe(
        finalize(() => {
          commit(SET_FORM_LOADING_MODE, false);
        }),
      )
      .subscribe(
        (data) => {
          eventsHub.$notify({
            group: 'frd',
            // duration: -1, // stays for unlimited time infinite for testing
            type: 'success',
            text: '' + eventsHub.$t('form101.wizard.formSubmitSuccessfully'),
          });
          if (isFinal) {
            commit(SET_FORM_ERRORS_OPEN, false);
            commit(SET_OPEN_CONFIRM_FORM_DIALOG, true);
          } else {
            if (state.formErrorsShow) {
              commit(SET_FORM_ERRORS_OPEN, true);
              commit(SET_FORM_ERRORS, null);
            }
            // do something on success
          }
        },
        error => {
          commit(SET_FORM_ERRORS, error.validationMessages || error.messages);
          commit(SET_FORM_ERRORS_SHOW, true);
          commit(SET_FORM_ERRORS_OPEN, true);
        },
      );
  },

  [CHECK_MISSING_DOCUMENTS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
  ) => {
    commit(SET_FORM_LOADING_MODE, true);
    return new Promise((resolve, reject) => {
      formService.checkMissingDocuments(state.form101Data)
        .pipe(
          finalize(() => commit(SET_FORM_LOADING_MODE, false)),
        )
        .subscribe(
          (data) => {
            return resolve(true);
          },
          error => {
            // if (error.missingDocuments) {
            commit(DOCUMENT_ROOT + SET_MISSING_DOCUMENTS, error.missingDocuments, { root: true });
            // }
            return reject(error.validationMessages || error.messages);
          },
        );
    });
  },

  [GET_CITIES]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    taxYear: number,
  ) => {
    formService.getCitiesList(taxYear)
      .subscribe(
        (data) => {
          commit(SET_CITIES, data);
        },
      );
  },

  [GET_SETTLEMENTS]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    taxYear: number,
  ) => {
    formService.getSettlementsList(taxYear)
      .subscribe(
        (data) => {
          commit(SET_SETTLEMENTS, data);
        },
      );
  },

  [UPDATE_USER_OFFICE]: (
    { commit, dispatch, state, rootGetters }: ActionContext<FormState, RootState>,
    { formId, officeNum }: { formId: number, officeNum: number },
  ) => {
    return new Promise((resolve, reject) => {
      formService.updateUserOffice(officeNum, formId)
        .subscribe(
          (data) => {
            commit(AUTHORIZATION_ROOT + SET_TOKENS,
              { accessToken: data.accessToken, refreshToken: data.refreshToken },
              { root: true },
            );
            return resolve();
          },
          (error) => {
            commit(SET_GLOBAL_ERROR, { response: { status: 400 } }, { root: true });
            return reject(error);
          },
        );
    });

  },

};
